import { Payment, VoidPaymentRequest } from '../types/payment.types';
import { formatDateTime } from '../i18n/format';
import { readAdminLang } from '../i18n/admin';
import { isVoidedPayment } from './refunds';
import { formatMoneyFixed } from './money';

/**
 * Check if a payment was made on the same day (today)
 * @param paymentDate ISO date string
 * @returns true if payment was made today
 */
export const isSameDay = (paymentDate: string): boolean => {
  const payment = new Date(paymentDate);
  const today = new Date();
  
  return (
    payment.getDate() === today.getDate() &&
    payment.getMonth() === today.getMonth() &&
    payment.getFullYear() === today.getFullYear()
  );
};

/**
 * Format payment date with the month written out («Oct 2, 2026, 11:00 AM»), in the business clock
 * @param date ISO date string or null
 * @returns formatted date string or 'N/A' if null
 */
export const formatPaymentDate = (date: string | null): string => {
  if (!date) return 'N/A';
  
  // SAM-739: month written out («Oct 2, 2026, 11:00 AM»), never dd/mm/yyyy.
  return formatDateTime(date, readAdminLang());
};

/**
 * Format a payment amount as "RD$ 2.00" (two decimals always; DOP unless another currency is given).
 * @returns the formatted amount, or 'N/A' if there is none
 */
export const formatCurrency = (amount: number | null, currency: string | null | undefined = 'DOP'): string => {
  if (amount === null || amount === undefined) return 'N/A';
  return formatMoneyFixed(amount, currency || 'DOP') || 'N/A';
};

/**
 * The last four characters of a stored card value. The payments tables keep the last four digits (`cardLast4`), but a
 * record can carry a longer number; nothing longer than four ever reaches the screen or a search box (SAM-1281).
 */
export const lastFour = (card: string | null | undefined): string => {
  const digits = (card ?? '').replace(/\s+/g, '');
  return digits.slice(-4);
};

/**
 * Format card number as **** XXXX
 * @param last4 last 4 digits of card or null (a longer value is cut to its last four)
 * @returns formatted card number or 'N/A' if null
 */
export const formatCardNumber = (last4: string | null): string => {
  const four = lastFour(last4);
  if (!four) return 'N/A';
  return `**** ${four}`;
};

/**
 * Check if a payment can be voided
 * A payment can be voided if:
 * - Status is 'success'
 * - Payment was made today (same day restriction)
 * @param payment Payment object
 * @returns true if payment can be voided
 */
export const canVoidPayment = (payment: Payment): boolean => {
  if (!payment.payment_status || !payment.payment_date) return false;
  return payment.payment_status === 'success' && isSameDay(payment.payment_date);
};

/**
 * The gateway as the payments tables write it. The stored value is `carnet` (the Cardnet integration's historical
 * spelling) or `portal`; the name on screen is the bank's: Cardnet.
 */
export const gatewayLabel = (gateway: string | null | undefined): string => {
  if (!gateway) return '—';
  const g = gateway.toLowerCase();
  if (g === 'carnet' || g === 'cardnet') return 'Cardnet';
  if (g === 'portal') return 'Portal';
  return gateway.charAt(0).toUpperCase() + gateway.slice(1);
};

/**
 * The business a payment is voided (and checked for the void permission) against: the PAYMENT's own business.
 * The tenant-wide listing (the admin's view of every business) carries `business_id` on each row; the selected
 * business is only the fallback for the per-business listing, which omits it. Sending the selected business
 * for another business's payment is refused: 409/403 "business_id does not match the business that owns this
 * transaction".
 */
export const paymentBusinessId = (payment: Payment, selectedBusinessId?: string | null): string | null =>
  payment.business_id || selectedBusinessId || null;

/** The `POST /business_access/void` body for a payment, or null when it cannot be built. The amount is required by the API. */
export const buildVoidRequest = (payment: Payment, selectedBusinessId?: string | null): VoidPaymentRequest | null => {
  const business_id = paymentBusinessId(payment, selectedBusinessId);
  if (!business_id || !payment.transaction_reference || payment.payment_amount === null) return null;
  return { transaction_reference: payment.transaction_reference, business_id, amount: payment.payment_amount };
};

/** The payment of a pass: the latest one the business listing holds for that `business_access_id`. */
export const paymentOfPass = (payments: readonly Payment[] | undefined | null, passId: string | null | undefined): Payment | null => {
  if (!payments || !passId) return null;
  const own = payments.filter((p) => String(p.business_access_id) === String(passId));
  if (!own.length) return null;
  const at = (p: Payment) => new Date(p.payment_date || p.createdAt || 0).getTime() || 0;
  return own.reduce((latest, p) => (at(p) >= at(latest) ? p : latest));
};

export type PaymentChip = 'paid' | 'voided' | 'refunded' | 'partiallyRefunded' | 'failed';

/**
 * What the payment is, apart from what the pass is: Paid, Voided, Refunded, Partially refunded or Failed. The refund
 * ledger (`refund_status`, SAM-466) wins when the backend sends it; otherwise the stored `payment_status`.
 */
export const paymentChipOf = (p: Pick<Payment, 'payment_status' | 'refund_status' | 'refunds'>): PaymentChip | null => {
  if (isVoidedPayment(p)) return 'voided';
  const s = p.refund_status ?? p.payment_status;
  if (s === 'voided') return 'voided';
  if (s === 'refunded') return 'refunded';
  if (s === 'partially_refunded') return 'partiallyRefunded';
  if (s === 'failed') return 'failed';
  if (s === 'success' || s === 'paid') return 'paid';
  return null;
};

/**
 * What the business keeps of a payment: the amount for a charge that stands, the amount minus what was refunded for
 * a partial refund, and nothing for a void, a full refund or a declined charge. `remaining` (SAM-1186) is the
 * server's own figure; before it is deployed the difference is computed here.
 */
export const netPaidAmount = (p: Payment): number => {
  const chip = paymentChipOf(p);
  if (chip !== 'paid' && chip !== 'partiallyRefunded') return 0;
  if (typeof p.remaining === 'number') return Math.max(0, p.remaining);
  const refunded = chip === 'partiallyRefunded' ? Number(p.refunded_amount) || 0 : 0;
  return Math.max(0, (Number(p.payment_amount) || 0) - refunded);
};
